'use client';
import { useState } from 'react';
import Slider, { Settings } from 'react-slick';
import NearTheVillageSectionCarouselItem from './CarouselItem';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import useObserverContext from '@/hooks/useObserverContext';
import { InView } from 'react-intersection-observer';
import { IVillageDictionary } from '@/dictionaries/pages/village/types';
import { VillageSurrounding } from '@/services/types';
import './styles.css';
import { cn } from '@/lib/utils';

type Props = {
  dict: IVillageDictionary;
  lang: string;
  villageSurroundings: VillageSurrounding[];
  villaInPuglia: boolean;
};

export default function NearTheVillageSectionCarousel({
  dict,
  lang,
  villageSurroundings,
  villaInPuglia,
}: Props) {
  const [sliderRef, setSliderRef] = useState<Slider | null>(null);
  const { handleCurrentSection } = useObserverContext();

  const mainSurroudings = villageSurroundings.filter((surrouding) => {
    return surrouding.isMain;
  });

  const shouldShowCarouselAccessories = mainSurroudings.length > 1;

  const settings: Settings = {
    dots: shouldShowCarouselAccessories,
    infinite: shouldShowCarouselAccessories,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    dotsClass: 'carousel-dots',
    centerMode: true,
    centerPadding: '13.104%',
    responsive: [
      {
        breakpoint: 1023,
        settings: {
          dots: shouldShowCarouselAccessories,
          infinite: shouldShowCarouselAccessories,
          centerMode: false,
          centerPadding: undefined,
        },
      },
    ],
  };

  function handleInViewOnChange(inView: boolean) {
    if (inView) {
      handleCurrentSection('nearby');
    } else {
      handleCurrentSection('');
    }
  }

  return (
    <InView
      as="div"
      className="relative w-full desktop:h-full"
      onChange={(inView) => handleInViewOnChange(inView)}>
      {shouldShowCarouselAccessories && (
        <>
          <button
            id="nv-carrousel-previous"
            aria-label="Go to carousel previous item"
            className="rounded-full z-10 text-zinc-400 flex justify-center
        group items-center bg-white shadow-lg overflow-hidden absolute top-[42%] left-0  translate-y-[-50%] mt-[-50px] w-11 h-[44px] desktop:hidden"
            type="button"
            onClick={sliderRef?.slickPrev}>
            <FiChevronLeft className="w-6 h-6 desktop:hidden stroke-slate-600 opacity-30 group-hover:opacity-20 transition" />
          </button>
          <button
            id="nv-carrousel-next"
            aria-label="Go to carousel next item"
            className="rounded-full z-10 text-zinc-400 flex justify-center
        group items-center bg-white shadow-lg overflow-hidden absolute top-[42%] right-0 translate-y-[-50%] mt-[-50px] w-11 h-[44px] desktop:hidden"
            type="button"
            onClick={sliderRef?.slickNext}>
            <FiChevronRight className="w-6 h-6 stroke-slate-600 opacity-30 group-hover:opacity-20 transition" />
          </button>
        </>
      )}
      <Slider
        ref={setSliderRef}
        className={cn(
          'near-the-village-carousel mx-[-0.9375rem] tablet:-mx-9 desktop:-mx-20',
          {
            '[&_button]:!hidden': !shouldShowCarouselAccessories,
          },
        )}
        {...settings}>
        {mainSurroudings.map((surrouding) => (
          <NearTheVillageSectionCarouselItem
            key={surrouding.surrounding.location.latitude}
            lang={lang}
            dict={dict}
            sliderRef={sliderRef}
            surrounding={surrouding}
            villaInPuglia={villaInPuglia}
          />
        ))}
      </Slider>
    </InView>
  );
}
